import {
    isGUID,
    type CallExpressionShape,
    type Diagnostics,
    type Factory,
    type ObjectShape,
    type RecordId,
    type Shape,
} from '@servicenow/sdk-build-core'
import { generateRagInputs, generateToolInputs, isToolInputType } from './tool-input-helper'
import type { RagInputType } from './aia-types'
import { extractSysId } from './ai-agent-transform-helper'
import {
    REACT_STRATEGY_SYS_ID,
    WEB_SEARCH_TOOL_SYS_ID,
    WEB_SEARCH_DEFAULT_INPUTS,
    TOOL_TYPE_TO_TARGET_TABLE_MAP,
    DEFAULT_CONTEXT_PROCESSING_SCRIPT,
    SN_AIA_LTM_CATEGORY_MAPPING,
    SYS_AGENT_ACCESS_ROLE_MAPPING,
    SYS_USER_ROLE,
} from './constants'
import { generateCrudScript } from './crud-script-templates/crud-script-generator'

/**
 * Helper function to extract target_document based on tool type
 * @param toolDetails - The tool details object shape
 * @param toolType - The type of the tool
 * @returns The sys_id of the target document or empty string
 */
function extractTargetDocument(toolDetails: ObjectShape, toolType: string | undefined): string {
    if (!toolType) {
        return ''
    }

    // Map tool type to the corresponding field name
    const fieldMap: Record<string, string> = {
        capability: 'capabilityId',
        subflow: 'subflowId',
        action: 'flowActionId',
        catalog: 'catalogItemId',
        topic: 'virtualAgentId',
        topic_block: 'virtualAgentId',
    }

    const fieldName = fieldMap[toolType]
    if (!fieldName) {
        // For crud, script, and other types, check targetDocument
        return extractSysId(toolDetails.get('targetDocument')) || ''
    }

    // Extract the sys_id from the type-specific field
    const fieldValue = toolDetails.get(fieldName)
    return extractSysId(fieldValue) || ''
}

/**
 * Helper function to serialize a shape to JSON string
 * @param shape - The shape to serialize
 * @param toolType - The tool type (e.g., 'rag', 'crud') to determine how to serialize inputs
 * @param defaultValue - Default value to return if shape is undefined or empty
 * @returns JSON string representation of the shape
 */
function serializeToJson(shape: Shape | undefined, toolType: string | undefined, defaultValue = ''): string {
    if (!shape) {
        return defaultValue
    }

    if (shape.isArray()) {
        // For arrays, map each element's value and stringify the result
        return JSON.stringify(shape.getElements().map((element) => element.getValue()))
    } else if (shape.isObject()) {
        const objectValue = shape.getValue()

        // Check tool type to determine serialization strategy
        if (toolType === 'crud' && isToolInputType(objectValue)) {
            // Transform ToolInputType to complex inputs JSON
            const transformedInputs = generateToolInputs(objectValue)
            return JSON.stringify(transformedInputs)
        }

        if (toolType === 'search_retrieval') {
            // Transform RagInputType to RAG inputs JSON
            const transformedInputs = generateRagInputs(objectValue as RagInputType)
            return JSON.stringify(transformedInputs)
        }

        // For regular objects, stringify the object value
        return JSON.stringify(objectValue)
    }

    // For primitive values
    const value = shape.getValue()
    return value !== undefined ? String(value) : defaultValue
}

/**
 * Extracts inputs array from a Shape based on its type
 * @param inputsShape - The inputs shape (array, string, object, or ToolInputType)
 * @returns Array of input objects or empty array
 */
function extractInputsArray(inputsShape: Shape): unknown[] {
    // Handle array shape
    if (inputsShape.isArray()) {
        return inputsShape.getElements().map((element) => element.getValue())
    }

    // Handle string shape (JSON string)
    if (inputsShape.isString()) {
        const inputsStr = inputsShape.getValue()
        if (!inputsStr || inputsStr === '' || inputsStr === '[]') {
            return []
        }
        return JSON.parse(inputsStr)
    }

    // Handle object shape
    if (inputsShape.isObject()) {
        const value = inputsShape.getValue()

        // Check if this is a CRUD ToolInputType object
        if (isToolInputType(value)) {
            return generateToolInputs(value)
        }

        // Try to extract inputFields from regular object
        const objShape = inputsShape.asObject()
        const inputFieldsShape = objShape.get('inputFields')
        if (!inputFieldsShape) {
            return []
        }

        if (inputFieldsShape.isArray()) {
            return inputFieldsShape.getElements().map((element) => element.getValue())
        }

        const fieldValue = inputFieldsShape.getValue()
        return Array.isArray(fieldValue) ? fieldValue : []
    }

    // If we reach here, the shape type is not recognized
    return []
}

/**
 * Transforms a single input object to schema entry format
 * Extracts only name, description, and mandatory fields
 * @param input - The input object to transform
 * @returns Schema entry with name, description, and mandatory fields
 */
function transformInputToSchemaEntry(input: unknown): { name?: string; description?: string; mandatory?: boolean } {
    const schemaEntry: { name?: string; description?: string; mandatory?: boolean } = {}

    if (typeof input !== 'object' || input === null) {
        return schemaEntry
    }

    const inputObj = input as Record<string, unknown>

    // Extract name
    if (inputObj['name'] !== undefined) {
        schemaEntry.name = String(inputObj['name'])
    }

    // Extract description (with special handling for crudInputs)
    if (inputObj['description'] !== undefined) {
        if (inputObj['name'] === 'crudInputs') {
            schemaEntry.description =
                'CRUD Input Variables. These are already pre-defined. Under no circumstances should you prompt the user for this information or attempt to change these values.'
        } else {
            schemaEntry.description = String(inputObj['description'])
        }
    }

    // Extract mandatory flag
    if (inputObj['mandatory'] !== undefined) {
        schemaEntry.mandatory = Boolean(inputObj['mandatory'])
    }

    return schemaEntry
}

/**
 * Helper function to generate input_schema from inputs array
 * Extracts only name, description, and mandatory fields from each input object
 * @param inputsShape - The inputs shape (array of objects or ToolInputType object)
 * @returns JSON string representation of the input schema
 */
function generateInputSchemaFromInputs(inputsShape: Shape | undefined): string {
    if (!inputsShape) {
        return '[]'
    }

    try {
        // Extract inputs array based on shape type
        const inputsArray = extractInputsArray(inputsShape)

        // If no inputs, return empty array
        if (inputsArray.length === 0) {
            return '[]'
        }

        // Transform each input to schema entry format
        const schema = inputsArray.map(transformInputToSchemaEntry)

        return JSON.stringify(schema)
    } catch {
        // If parsing fails, return empty array
        return '[]'
    }
}

/**
 * Creates an agent record with the provided configuration
 * @param factory - The record factory
 * @param callExpression - The source call expression
 * @param arg - The main argument shape
 * @param explicitId - The explicit ID for the agent record
 * @param computedInternalName - The computed internal name for the agent
 * @param tableName - The table name for the agent record
 * @returns The created agent record
 */
export async function createAgentRecord(
    factory: Factory,
    callExpression: CallExpressionShape,
    computedInternalName: string,
    tableName: string,
    publishedInstructions: string,
    aclId?: string
) {
    const arg = callExpression.getArgument(0).asObject()
    const explicitId = arg.get('$id')

    return await factory.createRecord({
        source: callExpression,
        table: tableName,
        explicitId: explicitId,
        properties: arg.transform(({ $ }) => ({
            name: $.from('name'),
            description: $.from('description'),
            role: $.from('agentRole'),
            record_type: $.from('recordType').def('template'),
            agent_type: $.from('agentType').def('internal'),
            instructions: $.val(publishedInstructions),
            sys_domain: $.from('sysDomain').def('global'),
            strategy: $.val(REACT_STRATEGY_SYS_ID),
            processing_message: $.from('processingMessage'),
            post_processing_message: $.from('postProcessingMessage'),
            context_processing_script: $.from('contextProcessingScript')
                .def(DEFAULT_CONTEXT_PROCESSING_SCRIPT)
                .toCdata(),
            advanced_mode: $.from('advancedMode').def(false),
            channel: $.from('channel').def('nap_and_va'),
            condition: $.from('condition'),
            compiled_handbook: $.from('compiledHandbook').def(''),
            external: $.from('external').def(false),
            parent: $.from('parent').def(''),
            source_id: $.from('sourceId').def(''),
            internal_name: $.val(computedInternalName),
            external_agent_configuration: $.from('externalAgentConfiguration').def(''),
            active: $.from('active').def(true),
            acl: $.val(aclId ?? ''),
        })),
    })
}

/**
 * Creates an agent config record with the provided configuration
 * @param factory - The record factory
 * @param callExpression - The source call expression
 * @param agentRecordId - The ID of the parent agent record
 * @param arg - The main argument shape with flattened agent config fields
 * @param tableName - The table name for the config record
 * @returns The created agent config record
 */
export async function createAgentConfigRecord(
    factory: Factory,
    callExpression: CallExpressionShape,
    agentRecordId: RecordId,
    arg: ObjectShape,
    tableName: string
) {
    return await factory.createRecord({
        source: callExpression,
        table: tableName,
        properties: arg.transform(({ $ }) => ({
            agent: $.val(agentRecordId),
            active: $.from('active').def(true),
            public: $.from('public').def(false),
            agent_learning: $.from('agentLearning').def(false),
            doc_url: $.from('docUrl'),
            icon_url: $.from('iconUrl'),
            run_as_user: $.from('runAsUser').def(''),
            sys_overrides: $.from('agentConfigSysOverrides').def(''),
            agent_descriptor: $.from('agentDescriptor').def(''),
            sys_domain: $.def('global'),
        })),
    })
}

/**
 * Extracts published instructions from versionDetails array
 * @param arg - The argument shape containing versionDetails
 * @returns Object containing published instructions and count of published versions
 */
export function extractPublishedInstructions(arg: ObjectShape): { instructions: string; publishedCount: number } {
    const versionDetailsArray = arg.get('versionDetails')?.ifArray()
    if (!versionDetailsArray) {
        return { instructions: '', publishedCount: 0 }
    }

    let publishedInstructions = ''
    let publishedCount = 0
    const versionElements = versionDetailsArray.getElements()

    for (const versionElement of versionElements) {
        const versionObj = versionElement.asObject()
        const state = versionObj.get('state')?.ifString()?.getValue()
        if (state === 'published') {
            publishedCount++
            // Store the last published version's instructions
            publishedInstructions = versionObj.get('instructions')?.ifString()?.getValue() || ''
        }
    }

    return { instructions: publishedInstructions, publishedCount }
}

/**
 * Creates agent version records from versionDetails array
 * @param factory - The record factory
 * @param callExpression - The source call expression
 * @param agentRecordId - The ID of the parent agent record
 * @param tableName - The table name for the version record
 * @param agentTableName - The agent table name
 * @returns Array of created agent version records
 */
export async function createAgentVersionRecords(
    factory: Factory,
    callExpression: CallExpressionShape,
    agentRecordId: RecordId,
    tableName: string,
    agentTableName: string
) {
    const arg = callExpression.getArgument(0).asObject()
    const versionDetailsArray = arg.get('versionDetails')?.ifArray()

    // If versionDetails is provided as an array, create multiple version records
    if (versionDetailsArray) {
        const versionElements = versionDetailsArray.getElements()
        return await Promise.all(
            versionElements.map(async (versionElement) => {
                const versionDetails = versionElement.asObject()

                return await factory.createRecord({
                    source: callExpression,
                    table: tableName,
                    properties: versionDetails.transform(({ $ }) => ({
                        state: $.from('state').def('published'),
                        instructions: $.from('instructions'),
                        version_name: $.from('name').def('V1'),
                        condition: $.from('condition'),
                        target_id: $.val(agentRecordId),
                        sys_domain: $.def('global'),
                        version_number: $.from('number').def(1),
                        target_table: $.def(agentTableName),
                    })),
                })
            })
        )
    }

    // Fallback: create a single default version record if no versionDetails provided
    const defaultVersionRecord = await factory.createRecord({
        source: callExpression,
        table: tableName,
        properties: arg.transform(({ $ }) => ({
            state: $.def('draft'),
            instructions: $.def(''),
            version_name: $.from('name').def('V1'),
            target_id: $.val(agentRecordId),
            sys_domain: $.def('global'),
            version_number: $.def(1),
            target_table: $.def(agentTableName),
        })),
    })

    return [defaultVersionRecord]
}

/**
 * Resolve a single role value to sys_id
 * IMPORTANT: Only sys_ids (GUIDs) are supported for roleList
 * Role names are NOT supported - must use sys_ids
 * The role_list field is a List type, not a reference field, so role names won't resolve
 */
function resolveRoleToSysId(roleValue: Shape, diagnostics: Diagnostics): string | undefined {
    // If it's a Record, extract the sys_id
    const recordRef = roleValue.ifRecord()
    if (recordRef) {
        return recordRef.getId().getValue()
    }

    // Get string value
    const roleStr = roleValue.asString()?.getValue()
    if (!roleStr) {
        return undefined
    }

    // Check if it's a sys_id (GUID)
    if (isGUID(roleStr)) {
        return roleStr
    }

    // Role names are NOT supported - must use sys_ids
    diagnostics.error(
        roleValue,
        `roleList must contain only role sys_ids (GUIDs), not role names. ` +
            `Got: '${roleStr}'. Use the role sys_id when using roleList ` +
            `(e.g., '2831a114c611228501d4ea6c309d626d' for admin), or use roleMap for role names.`
    )
    return undefined
}

/**
 * Creates a data access configuration record and optional M2M role mapping records.
 * Supports `roleList` (sys_ids) and `roleMap` (role names). At least one must be non-empty unless `runAsUser` is set.
 */
export async function createDataAccessConfigRecord(
    factory: Factory,
    callExpression: CallExpressionShape,
    agentRecordId: RecordId,
    dataAccess: ObjectShape,
    tableName: string,
    agentTableName: string,
    diagnostics: Diagnostics,
    hasRunAsUser = false
) {
    const roleListArray = dataAccess.get('roleList')?.ifArray()
    const restrictionGUIDs: string[] = []

    if (roleListArray) {
        const roleElements = roleListArray.getElements()
        const resolvedSysIds = await Promise.all(
            roleElements.map((roleValue) => resolveRoleToSysId(roleValue, diagnostics))
        )
        for (const sysId of resolvedSysIds) {
            if (sysId) {
                restrictionGUIDs.push(sysId)
            }
        }
    }

    const roleListValue = restrictionGUIDs.join(',')

    const roleMapField = dataAccess.get('roleMap')
    const roleMapArray = roleMapField?.ifArray()
    const validatedRoleMapItems: Shape[] = []

    if (roleMapArray) {
        for (const role of roleMapArray.getElements()) {
            if (role.ifString()) {
                const roleValue = role.asString()!.getValue()
                if (isGUID(roleValue)) {
                    diagnostics.error(
                        roleMapField!,
                        `roleMap must contain role names, not sys_ids. ` +
                            `Got: '${roleValue}'. Use the role name (e.g., 'admin'), ` +
                            `or move the sys_id to dataAccess.roleList.`
                    )
                } else if (roleValue.trim() !== '') {
                    validatedRoleMapItems.push(role)
                }
            } else {
                validatedRoleMapItems.push(role)
            }
        }
    }

    if (!hasRunAsUser && restrictionGUIDs.length === 0 && validatedRoleMapItems.length === 0) {
        diagnostics.error(
            dataAccess,
            'dataAccess must have at least one of roleList or roleMap non-empty: ' +
                'provide sys_ids in roleList, role names in roleMap, or both.'
        )
    }

    const roleConfigRecord = await factory.createRecord({
        source: callExpression,
        table: tableName,
        properties: dataAccess.transform(({ $ }) => ({
            agent: $.val(agentRecordId.getValue()),
            agent_table: $.val(agentTableName),
            action: $.val('limit_to_roles'),
            role_list: $.val(roleListValue),
            description: $.from('description').def(''),
            name: $.val(agentRecordId.getValue()),
        })),
    })

    const records = [roleConfigRecord]

    for (const role of validatedRoleMapItems) {
        const roleReference = role.ifString()
            ? await factory.createReference({
                  source: role,
                  table: SYS_USER_ROLE,
                  keys: { name: role.asString()!.getValue() },
              })
            : role
        const mappingRecord = await factory.createRecord({
            source: role,
            table: SYS_AGENT_ACCESS_ROLE_MAPPING,
            properties: {
                agent_access_config: roleConfigRecord,
                role: roleReference,
            },
        })
        records.push(mappingRecord)
    }

    return records
}

/**
 * Creates a trigger configuration record
 * @param factory - The record factory
 * @param callExpression - The source call expression
 * @param triggerConfig - The trigger configuration shape
 * @param computedTriggerInternalName - The computed internal name for the trigger
 * @param tableName - The table name for the trigger config record
 * @returns The created trigger config record
 */
export async function createTriggerConfigRecord(
    factory: Factory,
    callExpression: CallExpressionShape,
    triggerConfig: ObjectShape,
    computedTriggerInternalName: string,
    tableName: string
) {
    const schedule = triggerConfig.get('schedule')?.ifObject()

    return await factory.createRecord({
        source: callExpression,
        table: tableName,
        properties: triggerConfig.transform(({ $ }) => ({
            active: $.from('active').def(false),
            business_rule: $.from('businessRule').def(''),
            channel: $.from('channel').def(''),
            condition: $.from('triggerCondition').def(''),
            description: $.from('description'),
            enable_discovery: $.from('enableDiscovery').def(false),
            internal_name: $.val(computedTriggerInternalName),
            name: $.from('name'),
            notification_script: $.from('notificationScript').def(''),
            objective_template: $.from('objectiveTemplate').def(''),
            portal: $.from('portal').def(''),
            profile: $.from('profile').def(''),
            run_as: $.from('runAs'),
            run_as_script: $.from('runAsScript').def(''),
            run_as_user: $.from('runAsUser'),
            run_dayofmonth: $.val(schedule?.get('runDayOfMonth')?.getValue()),
            run_dayofweek: $.val(schedule?.get('runDayOfWeek')?.getValue()),
            run_period: $.val(schedule?.get('repeatInterval')?.getValue()),
            run_start: $.val(schedule?.get('starting')?.getValue()),
            run_time: $.val(schedule?.get('time')?.getValue()),
            show_notifications: $.from('showNotifications').def(false),
            sys_domain: $.from('domain').def('global'),
            sys_overrides: $.from('sysOverrides').def(''),
            target_table: $.from('targetTable'),
            trigger_flow: $.from('triggerFlow').def(''),
            trigger_flow_definition_type: $.from('triggerFlowDefinitionType'),
            trigger_strategy: $.val(schedule?.get('triggerStrategy')?.getValue()),
            usecase: $.from('usecase').def(''),
            usecase_discovery: $.from('usecaseDiscovery').def(false),
        })),
    })
}

/**
 * Creates a trigger mapping record
 * @param factory - The record factory
 * @param callExpression - The source call expression
 * @param agentRecordId - The ID of the parent agent record
 * @param triggerConfig - The trigger configuration shape to extract m2mSysOverrides from
 * @param linkedTriggerConfigId - The ID of the linked trigger config record
 * @param tableName - The table name for the trigger mapping record
 * @returns The created trigger mapping record
 */
export async function createTriggerMappingRecord(
    factory: Factory,
    callExpression: CallExpressionShape,
    agentRecordId: RecordId,
    triggerConfig: ObjectShape,
    linkedTriggerConfigId: RecordId,
    tableName: string
) {
    return await factory.createRecord({
        source: callExpression,
        table: tableName,
        properties: triggerConfig.transform(({ $ }) => ({
            active: $.from('active').def(false),
            objective_template: $.from('objectiveTemplate').toCdata(),
            related_resource_record: $.val(agentRecordId),
            related_resource_table: $.val('sn_aia_agent'),
            sys_domain: $.from('domain').def('global'),
            sys_overrides: $.from('m2mSysOverrides').def(''),
            trigger_configuration: $.val(linkedTriggerConfigId),
        })),
    })
}

/**
 * Creates an agent tool M2M record (for linking to existing tools)
 * @param factory - The record factory
 * @param callExpression - The source call expression
 * @param agentRecordId - The ID of the parent agent record
 * @param toolConfig - The tool configuration shape combined from tool record and M2M
 * @param toolSysId - The sys_id of the existing tool to link to
 * @param tableName - The table name for the M2M record
 * @returns The created agent tool M2M record
 */
export async function createAgentToolM2MRecord(
    factory: Factory,
    callExpression: CallExpressionShape,
    agentRecordId: RecordId,
    toolConfig: ObjectShape,
    toolSysId: string,
    tableName: string
) {
    return await factory.createRecord({
        source: callExpression,
        table: tableName,
        properties: toolConfig.transform(({ $ }) => ({
            max_auto_executions: $.from('maxAutoExecutions').def(10),
            active: $.from('active').def(true),
            post_message: $.from('postMessage'),
            pre_message: $.from('preMessage'),
            sys_domain: $.def('global'),
            transformation_instructions: $.from('transformationInstructions'),
            execution_mode: $.from('executionMode').def('autopilot'),
            agent: $.val(agentRecordId),
            document_status: $.from('documentStatus'),
            tool: $.val(toolSysId),
            tool_attributes: $.from('toolAttributes').def('{}'),
            name: $.from('name'),
            output_transformation_strategy: $.from('outputTransformationStrategy'),
            inputs: $.from('inputs')
                .map((inputs) => {
                    // If this is the Web Search tool and inputs is empty, use default inputs
                    if (toolSysId === WEB_SEARCH_TOOL_SYS_ID) {
                        const inputsArray = inputs.ifArray()
                        if (!inputsArray || inputsArray.getElements().length === 0) {
                            return WEB_SEARCH_DEFAULT_INPUTS
                        }
                    }

                    // Get tool type from toolConfig
                    const toolType = toolConfig.get('type')?.ifString()?.getValue()
                    // Serialize the inputs with tool type context
                    return serializeToJson(inputs, toolType, '[]')
                })
                .def(toolSysId === WEB_SEARCH_TOOL_SYS_ID ? WEB_SEARCH_DEFAULT_INPUTS : '[]'),
            display_output: $.from('displayOutput').def(false),
            description: $.from('description'),
            timeout: $.from('timeout'),
            pre_run: $.from('preRun').def(false),
            post_processing_script: $.from('postProcessingScript').def(''),
            widgets: $.from('widgets').map((v) => {
                if (v.isArray()) {
                    return v
                        .getElements()
                        .map((w) => {
                            if (w.isRecord()) {
                                return w.getId().getValue()
                            }
                            return w.getValue()
                        })
                        .join(',')
                }
                return v.ifString()?.getValue() || ''
            }),
            // Use m2mSysOverrides for M2M record overrides, never fall back to sysOverrides
            // This ensures strict separation between tool record overrides and M2M record overrides
            sys_overrides: $.from('m2mSysOverrides').def(''),
        })),
    })
}

/**
 * Maps user-facing tool type to internal ServiceNow tool type
 * @param toolType - The user-facing tool type
 * @returns The internal ServiceNow tool type
 */
function mapToolTypeToInternal(toolType: string | undefined): string | undefined {
    if (toolType === 'search_retrieval') {
        return 'rag'
    }
    return toolType
}

/**
 * Creates an agent tool record
 * @param factory - The record factory
 * @param callExpression - The source call expression
 * @param toolDetails - The tool configuration shape combined from tool record and M2M
 * @param tableName - The table name for the tool record
 * @returns The created agent tool record
 */
export async function createAgentToolRecord(
    factory: Factory,
    callExpression: CallExpressionShape,
    toolDetails: ObjectShape,
    tableName: string
) {
    // Get the tool type to determine target_document_table
    const userToolType = toolDetails.get('type')?.ifString()?.getValue()
    // Map user-facing type to internal type (search_retrieval → rag)
    const toolType = mapToolTypeToInternal(userToolType)
    const targetDocumentTable = toolType ? TOOL_TYPE_TO_TARGET_TABLE_MAP[toolType] || '' : ''

    // Extract target_document based on tool type
    const targetDocument = extractTargetDocument(toolDetails, toolType)

    // Get inputs to generate input_schema
    const inputsShape = toolDetails.get('inputs')

    // Auto-generate CRUD script if not provided
    const autoGeneratedScript = generateCrudScript(inputsShape, toolType)

    return await factory.createRecord({
        source: callExpression,
        table: tableName,
        properties: toolDetails.transform(({ $ }) => ({
            name: $.from('name'),
            target_document: $.val(targetDocument),
            target_document_table: $.val(targetDocumentTable),
            script: autoGeneratedScript ? $.val(autoGeneratedScript).toCdata() : $.from('script').def('').toCdata(),
            post_processing_script: $.from('postProcessingScript').def(''),
            record_type: $.from('recordType').def('template'),
            description: $.from('description'),
            active: $.from('active').def(true),
            sys_domain: $.def('global'),
            sys_overrides: $.from('sysOverrides').def(''),
            type: $.val(toolType),
            input_schema: $.val(generateInputSchemaFromInputs(inputsShape)),
        })),
    })
}

/**
 * Creates a single sn_aia_ltm_category_mapping record linking an agent to a memory category
 * @param factory - The record factory
 * @param callExpression - The source call expression
 * @param agentRecordId - The ID of the parent agent record
 * @param categorySysId - The sys_id of the memory category to link
 * @returns The created category mapping record
 */
export async function createCategoryMappingRecord(
    factory: Factory,
    callExpression: CallExpressionShape,
    agentRecordId: RecordId,
    categorySysId: string
) {
    const arg = callExpression.getArgument(0).asObject()
    return await factory.createRecord({
        source: callExpression,
        table: SN_AIA_LTM_CATEGORY_MAPPING,
        properties: arg.transform(({ $ }) => ({
            entity_table: $.val('sn_aia_agent'),
            entity_id: $.val(agentRecordId),
            category: $.val(categorySysId),
        })),
    })
}
